import { getAllEventData } from '../firebase';
import { 
    fetchGroupsByCourseId, 
    fetchCoursesFromFirestore,
    fetchAdminsByCourseId 
} from '../admin';

// Cache duration in milliseconds
const CACHE_DURATION = {
    SHORT: 60 * 1000, // 1 minute
    MEDIUM: 5 * 60 * 1000, // 5 minutes
    LONG: 60 * 60 * 1000, // 1 hour
    VERY_LONG: 24 * 60 * 60 * 1000 // 24 hours
};

// Cached data store
const dataCache = {
    courses: new Map(),
    courseGroups: new Map(),
    groupEvents: new Map(),
    courseAdmins: new Map(),
    groupMembers: new Map(),
    courseData: new Map(),
};

// Helper function to calculate total working hours
const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const calculateDurationMinutes = (startTime, endTime, isCrossDate) => {
    if (!startTime || !endTime) return 0;
    
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    
    if (startMinutes === endMinutes) return 0;
    
    let durationMinutes;
    if (isCrossDate) {
        durationMinutes = (1440 - startMinutes) + endMinutes;
    } else {
        durationMinutes = endMinutes > startMinutes ? endMinutes - startMinutes : 0;
    }
    
    return durationMinutes;
};

const minutesToHours = (minutes) => {
    return minutes / 60;
};

// Calculate total working hours for activities
const calculateTotalWorkingHours = (activities) => {
    if (!activities || activities.length === 0) return 0;
    
    return activities.reduce((total, activity) => {
        const durationMinutes = calculateDurationMinutes(
            activity.startTime,
            activity.endTime,
            activity.isCrossDate || false
        );
        return total + minutesToHours(durationMinutes);
    }, 0);
};

// Cache management utilities
const getCacheItem = (cacheMap, key) => {
    const item = cacheMap.get(key);
    if (!item) return null;
    
    const { data, version, expiry } = item;
    const currentVersion = '1.0'; // Update this when data structure changes

    // Check if cache is valid using the stored expiry (set per-item by setCacheItem)
    if (version !== currentVersion || Date.now() > expiry) {
        cacheMap.delete(key);
        return null;
    }
    
    return data;
};

const setCacheItem = (cacheMap, key, data, duration = CACHE_DURATION.MEDIUM) => {
    cacheMap.set(key, {
        timestamp: Date.now(),
        data,
        version: '1.0',
        expiry: Date.now() + duration
    });
};

const clearCache = (cacheMap) => {
    cacheMap.clear();
};

// Add this helper function to count artifacts in events data
const countArtifactsInEvents = (events) => {
    let count = 0;
    if (!events || !Array.isArray(events)) return count;
    
    events.forEach(event => {
        // Count uploaded files as artifacts
        if (event.files && Array.isArray(event.files)) {
            count += event.files.length;
        }
        // Add other artifact types here if needed in the future
    });
    
    return count;
};

// Main Data Service
const AdminDataService = {
    /**
     * Get all courses for an admin user
     */
    getCourses: async (userEmail, forceRefresh = false) => {
        const cacheKey = `courses_${userEmail}`;
        
        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.courses, cacheKey);
            if (cachedData) {
                console.log('Using cached courses data');
                return cachedData;
            }
        }
        
        console.log('Fetching fresh courses data');
        try {
            const courses = await fetchCoursesFromFirestore(userEmail);
            setCacheItem(dataCache.courses, cacheKey, courses, CACHE_DURATION.MEDIUM);
            return courses;
        } catch (error) {
            console.error('Error fetching courses:', error);
            throw error;
        }
    },
    
    /**
     * Get all groups for a course with full details
     */
    getCourseGroups: async (courseId, includeEvents = false, forceRefresh = false) => {
        const cacheKey = `course_groups_${courseId}_${includeEvents}`;
        
        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.courseGroups, cacheKey);
            if (cachedData) {
                console.log('Using cached course groups data');
                return cachedData;
            }
        }
        
        console.log('Fetching fresh course groups data');
        try {
            // First get basic group data
            const groups = await fetchGroupsByCourseId(courseId);
            
            // Enhance with additional data if requested
            if (includeEvents) {
                const twoDaysAgo = new Date();
                twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
                const twoDaysAgoStr = twoDaysAgo.toISOString().split("T")[0];

                const enhancedGroups = await Promise.all(
                    groups.map(async (group) => {
                        const allEvents = await getAllEventData(group.id);
                        const workingHours = calculateTotalWorkingHours(allEvents);
                        const numLastTwoDayEvent = allEvents.filter(
                            e => e.date && e.date >= twoDaysAgoStr
                        ).length;

                        return {
                            ...group,
                            numLastTwoDayEvent,
                            numEvent: allEvents.length,
                            allEvents,
                            workingHours
                        };
                    })
                );
                
                setCacheItem(dataCache.courseGroups, cacheKey, enhancedGroups, CACHE_DURATION.SHORT);
                return enhancedGroups;
            }
            
            setCacheItem(dataCache.courseGroups, cacheKey, groups, CACHE_DURATION.MEDIUM);
            return groups;
        } catch (error) {
            console.error('Error fetching course groups:', error);
            throw error;
        }
    },
    
    /**
     * Get all events for a group with analysis data
     */
    getGroupEvents: async (groupId, forceRefresh = false) => {
        const cacheKey = `group_events_${groupId}`;
        
        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.groupEvents, cacheKey);
            if (cachedData) {
                console.log('Using cached group events data');
                return cachedData;
            }
        }
        
        console.log('Fetching fresh group events data');
        try {
            const events = await getAllEventData(groupId);
            const workingHours = calculateTotalWorkingHours(events);
            const artifactsCount = countArtifactsInEvents(events);
            
            const result = {
                events,
                count: events.length,
                workingHours,
                artifactsCount,
            };
            
            setCacheItem(dataCache.groupEvents, cacheKey, result, CACHE_DURATION.SHORT);
            return result;
        } catch (error) {
            console.error('Error fetching group events:', error);
            throw error;
        }
    },
    
    /**
     * Get all admins for a course
     */
    getCourseAdmins: async (courseId, forceRefresh = false) => {
        const cacheKey = `course_admins_${courseId}`;

        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.courseAdmins, cacheKey);
            if (cachedData) {
                console.log('Using cached course admins data');
                return cachedData;
            }
        }

        console.log('Fetching fresh course admins data');
        try {
            const admins = await fetchAdminsByCourseId(courseId);
            setCacheItem(dataCache.courseAdmins, cacheKey, admins, CACHE_DURATION.LONG);
            return admins;
        } catch (error) {
            console.error('Error fetching course admins:', error);
            throw error;
        }
    },

    getCourseConfig: async (courseId, forceRefresh = false) => {
        const cacheKey = `course_config_${courseId}`;

        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.courses, cacheKey);
            if (cachedData) {
                console.log('Using cached course config');
                return cachedData;
            }
        }

        console.log('Fetching fresh course config');
        try {
            const { getCourseConfig } = await import('../admin');
            const config = await getCourseConfig(courseId);
            setCacheItem(dataCache.courses, cacheKey, config, CACHE_DURATION.MEDIUM);
            return config;
        } catch (error) {
            console.error('Error fetching course config:', error);
            throw error;
        }
    },

    /**
     * Get comprehensive data for all groups in a course for team dynamics analysis
     */
    getCourseTeamDynamicsData: async (courseId, forceRefresh = false) => {
        const cacheKey = `team_dynamics_${courseId}`;
        
        if (!forceRefresh) {
            const cachedData = getCacheItem(dataCache.courseData, cacheKey);
            if (cachedData) {
                console.log('Using cached team dynamics data');
                return cachedData;
            }
        }
        
        console.log('Fetching fresh team dynamics data');
        try {
            // Reuse cached groups-with-events data if available — avoids N+1 Firebase calls
            const cachedGroupsWithEvents = getCacheItem(dataCache.courseGroups, `course_groups_${courseId}_true`);

            let groupsData;
            if (cachedGroupsWithEvents) {
                console.log('Deriving team dynamics from cached course groups');
                groupsData = cachedGroupsWithEvents.map(group => ({
                    groupId: group.id,
                    name: group.groupName,
                    members: group.members || [],
                    eventsCount: group.allEvents?.length || 0,
                    workingHours: group.workingHours || 0,
                    artifactsCount: countArtifactsInEvents(group.allEvents || []),
                }));
            } else {
                // Fallback: fetch all groups and their events fresh
                const groups = await fetchGroupsByCourseId(courseId);
                groupsData = await Promise.all(
                    groups.map(async (group) => {
                        const events = await getAllEventData(group.id);
                        const workingHours = calculateTotalWorkingHours(events);
                        const artifactsCount = countArtifactsInEvents(events);
                        return {
                            groupId: group.id,
                            name: group.groupName,
                            members: group.members || [],
                            eventsCount: events.length,
                            workingHours,
                            artifactsCount,
                            events,
                        };
                    })
                );
            }
            
            const result = {
                groups: groupsData,
                activitiesCounts: groupsData.map(g => g.eventsCount),
                workingHours: groupsData.map(g => g.workingHours),
                artifactsCounts: groupsData.map(g => g.artifactsCount),
                courseId
            };
            
            setCacheItem(dataCache.courseData, cacheKey, result, CACHE_DURATION.SHORT);
            return result;
        } catch (error) {
            console.error('Error fetching team dynamics data:', error);
            throw error;
        }
    },
    
    /**
     * Clear all cached data
     */
    clearAllCache: () => {
        clearCache(dataCache.courses);
        clearCache(dataCache.courseGroups);
        clearCache(dataCache.groupEvents);
        clearCache(dataCache.courseAdmins);
        clearCache(dataCache.groupMembers);
        clearCache(dataCache.courseData);
        console.log('All cache cleared');
    },
    
    /**
     * Clear specific cache
     */
    clearCache: (cacheType) => {
        if (dataCache[cacheType]) {
            clearCache(dataCache[cacheType]);
            console.log(`Cache for ${cacheType} cleared`);
        }
    },

    /**
     * Clear course-specific caches when course config changes
     */
    clearCourseCache: (courseId) => {
        const courseKeys = [
            `courses_`,
            `course_groups_${courseId}`,
            `course_config_${courseId}`,
            `course_admins_${courseId}`,
            `team_dynamics_${courseId}`,
        ];

        courseKeys.forEach(pattern => {
            for (const [key] of dataCache.courses) {
                if (key.includes(pattern)) {
                    dataCache.courses.delete(key);
                }
            }
            for (const [key] of dataCache.courseGroups) {
                if (key.includes(pattern)) {
                    dataCache.courseGroups.delete(key);
                }
            }
            for (const [key] of dataCache.courseData) {
                if (key.includes(pattern)) {
                    dataCache.courseData.delete(key);
                }
            }
        });

        console.log(`All caches cleared for course ${courseId}`);
    }
};

export default AdminDataService; 